import { Controller } from "@hotwired/stimulus"
import { Socket } from "phoenix"
import { simpleMarkdownToHtml } from "../../helpdesk/src/markdown-utils.js"

// Stimulus controller do rozmów z LLM przez Phoenix WebSocket
//
// Użycie w widoku:
//   <div data-controller="llm-chat"
//        data-llm-chat-socket-url-value="ws://localhost:4000/noe/ai/socket"
//        data-llm-chat-token-value="<%= noe_llm_api_token %>"
//        data-llm-chat-vector-db-id-value="<%= @noe_vector_db.id %>">
//     <textarea data-llm-chat-target="input"></textarea>
//     <button data-action="click->llm-chat#ask">Zapytaj</button>
//     <div data-llm-chat-target="output"></div>
//     <div data-llm-chat-target="status"></div>
//   </div>
export default class extends Controller {
  static targets = ["input", "output", "status", "duration", "meta", "context", "askButton", "spinner", "systemPrompt", "connectorSelect", "embeddingConnectorSelect", "contextLimit"]
  static values = {
    socketUrl: String,
    token: String,
    vectorDbId: String,
    // Testuj AI z desku i bazy wiedzy - tylko źródła widgetu
    sourceTypes: Array,
    knowledgeBaseIds: Array,
  }

  connect() {
    this.channel = null
    this.socket = null
    this.connectToPhoenix()
  }

  disconnect() {
    if (this.channel) this.channel.leave()
    if (this.socket) this.socket.disconnect()
  }

  connectToPhoenix() {
    const url = this.socketUrlValue
    if (!url) {
      this.showStatus("Brak konfiguracji WebSocket", "error")
      return
    }

    this.socket = new Socket(url, { params: { token: this.tokenValue } })
    this.socket.connect()

    this.socket.onError(() => this.showStatus("Błąd połączenia z Phoenix", "error"))
    this.socket.onClose(() => this.showStatus("Rozłączono", "warning"))

    this.channel = this.socket.channel("noe:llm_chat:lobby", {})

    this.channel.on("answer", (payload) => this.handleAnswer(payload))
    this.channel.on("error", (payload) => this.handleError(payload))

    this.channel
      .join()
      .receive("ok", () => this.showStatus("Połączono z NoeAI", "ok"))
      .receive("error", (resp) => this.showStatus(`Błąd join: ${JSON.stringify(resp)}`, "error"))
  }

  ask() {
    const question = this.inputTarget.value.trim()
    if (!question) return
    if (!this.channel) {
      this.showStatus("Brak połączenia z Phoenix", "error")
      return
    }

    this.showStatus("Analizuję pytanie...", "loading")
    this.setLoading(true)
    this.clearOutput()

    const payload = {
      question,
      vector_db_id: this.vectorDbIdValue,
    }
    if (this.hasSystemPromptTarget && this.systemPromptTarget.value.trim()) {
      payload.system_prompt = this.systemPromptTarget.value.trim()
    }
    if (this.hasConnectorSelectTarget && this.connectorSelectTarget.value) {
      payload.connector_id = this.connectorSelectTarget.value
    }
    if (this.hasEmbeddingConnectorSelectTarget && this.embeddingConnectorSelectTarget.value) {
      payload.embedding_connector_id = this.embeddingConnectorSelectTarget.value
    }
    if (this.hasContextLimitTarget && this.contextLimitTarget.value) {
      payload.context_limit = parseInt(this.contextLimitTarget.value, 10)
    }
    if (this.sourceTypesValue.length > 0) {
      payload.source_types = this.sourceTypesValue
    }
    // pusta lista też się liczy - widget bez publicznej bazy wiedzy nie odpowiada z żadnej
    if (this.hasKnowledgeBaseIdsValue) {
      payload.knowledge_base_ids = this.knowledgeBaseIdsValue
    }
    this.channel.push("ask", payload)
  }

  setLoading(loading) {
    if (this.hasAskButtonTarget) this.askButtonTarget.disabled = loading
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !loading)
  }

  handleAnswer(payload) {
    this.showStatus("", "ok")
    this.setLoading(false)

    if (this.hasOutputTarget) {
      if (payload.no_answer) {
        this.outputTarget.innerHTML = "<div class=\"bg-amber-50 border border-amber-300 rounded-lg p-4 text-amber-800 text-sm\"><strong>AI nie zna odpowiedzi</strong><br>Baza wiedzy nie zawiera wystarczających informacji dla tego pytania.</div>"
      } else {
        this.outputTarget.innerHTML = simpleMarkdownToHtml(payload.answer)
      }
    }

    if (this.hasDurationTarget && payload.duration) {
      this.durationTarget.textContent = `${payload.duration}s`
    }

    if (this.hasMetaTarget) {
      const parts = []
      if (payload.model) parts.push(payload.model)
      if (payload.provider) parts.push(payload.provider)
      if (payload.context_count) parts.push(`kontekst: ${payload.context_count}`)
      if (payload.no_answer) parts.push("NO_ANSWER")
      this.metaTarget.textContent = parts.join(" · ")
    }

    if (this.hasContextTarget && payload.context && payload.context.length > 0) {
      this.contextTarget.innerHTML = this.renderContext(payload.context)
    }
  }

  renderContext(context) {
    const vdbId = this.vectorDbIdValue
    const items = context.map((c, i) => {
      const dist = c.distance ? parseFloat(c.distance).toFixed(4) : "—"
      const type = c.source_type ? c.source_type.split("::").pop() : ""
      const entryUrl = `/noe/vector_dbs/${vdbId}/vector_entries/${c.id}`
      return `<div class="bg-gray-50 rounded p-2 border border-gray-200 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-gray-500">#${i + 1}</span>
          <span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">${type}</span>
          <a href="${entryUrl}" class="text-xs text-blue-600 hover:text-blue-800">${c.id.substring(0, 8)}…</a>
          <span class="text-xs text-gray-400">source: ${c.source_id || "—"}</span>
        </div>
        <span class="text-xs text-gray-400 font-mono">distance: ${dist}</span>
      </div>`
    }).join("")

    return `<details class="mt-4">
      <summary class="text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900">Kontekst (${context.length})</summary>
      <div class="mt-2 space-y-1">${items}</div>
    </details>`
  }

  handleError(payload) {
    this.showStatus(payload.message || "Błąd AI", "error")
    this.setLoading(false)
  }

  showStatus(message, type) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message

    const classes = this.statusTarget.classList
    classes.remove("text-green-600", "text-red-600", "text-amber-600", "text-gray-500", "animate-pulse")

    switch (type) {
      case "ok":
        classes.add("text-green-600")
        break
      case "error":
        classes.add("text-red-600")
        break
      case "warning":
        classes.add("text-amber-600")
        break
      case "loading":
        classes.add("text-gray-500", "animate-pulse")
        break
    }
  }

  clearOutput() {
    if (this.hasOutputTarget) this.outputTarget.innerHTML = ""
    if (this.hasDurationTarget) this.durationTarget.textContent = ""
    if (this.hasMetaTarget) this.metaTarget.textContent = ""
    if (this.hasContextTarget) this.contextTarget.innerHTML = ""
  }
}
